import {
    type ComponentType,
    type ReactElement,
    isValidElement,
    type ReactNode,
    Children,
    Fragment
} from "react";
import type {
    MenubarElementsConstantType,
    MenubarMenusConstantType,
    MenubarKeysConstantType,
    MenubarMenuRecordType
} from "./type";
import {
    getDropdownMenuElement
} from "../dropdownMenu/util";

export const MENUBAR_SURFACE_SELECTOR = "[data-ncore-surface=\"popover\"]";

export const MENUBAR_ESCAPE_KEY = "Escape";

export const MENUBAR_SPACE_KEY = " ";

export const MENUBAR_HOME_KEY = "Home";

export const MENUBAR_END_KEY = "End";

export const MENUBAR_F10_KEY = "F10";

export const MENUBAR_ALT_KEY = "Alt";

const isFragmentElement = (child: ReactElement): child is ReactElement<{
    children?: ReactNode;
}> => child.type === Fragment;

const isElementOf = <P>(child: ReactNode, type: ComponentType<P>): child is ReactElement<P> => {
    return isValidElement(child) && child.type === type;
};

export const getMenubarElements = <P>({
    children,
    type
}: MenubarElementsConstantType<P>): Array<ReactElement<P>> => {
    const elements: Array<ReactElement<P>> = [];

    Children.toArray(children).forEach((child) => {
        if(!isValidElement(child)) {
            return;
        }

        if(isFragmentElement(child)) {
            elements.push(...getMenubarElements({
                children: child.props.children,
                type
            }));

            return;
        }

        if(isElementOf(child, type)) {
            elements.push(child);
        }
    });

    return elements;
};

export const getMenubarMenus = ({
    contentType,
    triggerType,
    children,
    menuType
}: MenubarMenusConstantType): Array<MenubarMenuRecordType> => {
    return getMenubarElements({
        type: menuType,
        children
    }).map((menu) => {
        const trigger = getMenubarElements({
            children: menu.props.children,
            type: triggerType
        })[0];

        const contents = getMenubarElements({
            children: menu.props.children,
            type: contentType
        });

        return {
            content: contents.map((content) => content.props.children),
            title: trigger?.props.title ?? menu.props.menuKey,
            isDisabled: trigger?.props.isDisabled === true,
            menuKey: menu.props.menuKey
        };
    });
};

export const getMenubarKeys = ({
    registry
}: MenubarKeysConstantType): Array<string> => {
    return Array.from(registry.entries()).sort(([, first], [, second]) => {
        const firstElement = getDropdownMenuElement(first.node);

        const secondElement = getDropdownMenuElement(second.node);

        if(!firstElement || !secondElement) {
            return 0;
        }

        return firstElement.compareDocumentPosition(secondElement) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    }).map(([key]) => key);
};
